import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../utils/api";
import { fallbackPlans } from "../utils/subscriptionPlans";

const sections = [
  [
    "About TheGameio",
    "TheGameio is an online gaming service at thegameio.ng. Explore racing, solitaire, word puzzles and skill-based games using a compatible device and an internet connection.",
  ],
  [
    "Acceptance and eligibility",
    "By accepting these Terms of Service, you agree to use TheGameio in accordance with these terms. You must be authorised to use the mobile number and payment method provided. If you are below the legal age to enter into an agreement, a parent or legal guardian must give permission and authorise any purchases.",
  ],
  ["Subscription plans", null],
  [
    "Consent, billing, and renewal",
    "Accepting these terms does not by itself confirm a payment. Review the applicable charges and renewal conditions in the subscription confirmation flow. Where a recurring subscription is offered, review its billing frequency and cancellation instructions before authorising it. Closing your browser does not cancel a recurring subscription.",
  ],
  [
    "Cancellation and billing enquiries",
    "For recurring subscriptions, follow the cancellation instructions supplied when subscribing or contact the billing provider identified in your payment confirmation. For billing or refund enquiries, retain your transaction reference and the mobile number used to subscribe. These terms do not limit any refund or cancellation rights available under applicable law.",
  ],
  [
    "Responsible use",
    "Use the service for personal entertainment. Do not misuse another person's account, attempt unauthorised access, distribute harmful software, interfere with the service, or use automated methods to exploit games. Keep your account details secure and provide accurate subscription information.",
  ],
  [
    "Games and ownership",
    "Games, artwork, branding, and other content belong to their respective owners. A subscription allows personal use of available content; it does not transfer ownership or permit unauthorised copying, resale, or distribution. The game catalogue may change over time.",
  ],
  [
    "Service availability",
    "Game performance depends on your device, browser, and internet connection. Access may occasionally be interrupted by maintenance or technical issues. Nothing in these terms excludes rights or responsibilities that cannot be excluded under applicable law.",
  ],
  [
    "Updates to these terms",
    "The latest service terms are available on this page. Review the terms and pricing displayed before each new subscription or renewal authorisation. Material changes affecting an existing subscription should be communicated before they take effect.",
  ],
];

export default function TermsOfService() {

  const [plans, setPlans] = useState(fallbackPlans);
  const [priceStatus, setPriceStatus] = useState("loading");

  const returnTo = "/games";

  useEffect(() => {
    let active = true;

    api
      .get("/auth/access-context")
      .then(({ data }) => {
        if (!active) return;

        if (!Array.isArray(data?.data?.plans) || !data.data.plans.length) {
          throw new Error("No plans");
        }

        setPlans(data.data.plans);
        setPriceStatus("live");
      })
      .catch(() => {
        if (active) {
          setPriceStatus("fallback");
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="min-h-screen bg-black px-4 py-7 text-[#afb2b7] sm:px-6">
      <article className="mx-auto max-w-[760px] rounded-2xl border border-white/[0.07] bg-[#0d1011] px-5 py-7 sm:px-10 sm:py-10">

        {/* Back Button */}
        <Link
          to={returnTo}
          className="text-sm font-medium text-slate-200 underline underline-offset-4 hover:text-yellow-400"
        >
          ← Back to subscription
        </Link>

        {/* Page Heading */}
        <div className="mt-6">
          <p className="text-xs uppercase">THEGAMEIO.NG</p>

          <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Terms of Service
          </h1>

          <p className="mt-4 text-sm leading-6">
            Please read these terms before choosing a subscription on
            thegameio.ng.
          </p>
        </div>

        {/* Terms Sections */}
        <div className="mt-7 space-y-5">
          {sections.map(([title, text], index) => (
            <section key={title}>
              <h2 className="text-lg font-bold text-white">
                {index + 1}. {title}
              </h2>

              <p className="mt-2 text-sm leading-6">
                {index === 2 ? (
                  <>
                    {plans
                      .map(
                        (plan) =>
                          `${plan.name} costs ₦${Number(
                            plan.amount
                          ).toLocaleString("en-NG")} for ${
                            plan.validityDays
                          } ${
                            plan.validityDays === 1 ? "day" : "days"
                          }.`
                      )
                      .join(" ")}

                    {" "}
                    Access is available for the selected subscription period.
                    Review your plan and the billing information shown before
                    confirming. Internet and mobile data charges are separate
                    and depend on your network provider.
                  </>
                ) : (
                  text
                )}
              </p>

              {index === 2 && priceStatus === "fallback" && (
                <p
                  role="status"
                  className="mt-2 text-xs leading-5"
                >
                  Standard prices shown. Confirm the current price on the
                  subscription screen before continuing.
                </p>
              )}
            </section>
          ))}
        </div>

        {/* Bottom Back Button */}
        <Link
          to={returnTo}
          className="mt-6 inline-flex min-h-[44px] items-center rounded-md bg-[#ffc21c] px-4 py-2.5 text-sm font-semibold text-black hover:bg-yellow-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-yellow-400"
        >
          Back to games
        </Link>

      </article>
    </main>
  );
}
